/**
 * Social Icons Utility - Single Source of Truth
 * Centralized platform to icon mapping for entire site
 */

import { 
  FaApple, 
  FaSpotify, 
  FaYoutube, 
  FaInstagram, 
  FaTwitter, 
  FaTiktok, 
  FaFacebook, 
  FaLinkedin,
  FaGlobe, 
  FaEnvelope 
} from 'react-icons/fa';
import { SiThreads } from 'react-icons/si';

/**
 * Platform to React Icon mapping
 * Used consistently across all components
 */
export const platformIcons = {
  apple_podcasts: FaApple,
  spotify: FaSpotify,
  youtube: FaYoutube,
  instagram: FaInstagram,
  x: FaTwitter,
  tiktok: FaTiktok,
  threads: SiThreads,
  facebook: FaFacebook,
  linkedin: FaLinkedin,
  website: FaGlobe,
  email: FaEnvelope
};

/**
 * Platform display names
 */
export const platformNames = {
  apple_podcasts: 'Apple Podcasts',
  spotify: 'Spotify',
  youtube: 'YouTube',
  instagram: 'Instagram',
  x: 'X',
  tiktok: 'TikTok',
  threads: 'Threads',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  website: 'Website',
  email: 'Email'
};

/**
 * Platform brand colors for styling
 */
export const platformColors = {
  apple_podcasts: '#A259FF',
  spotify: '#1DB954',
  youtube: '#FF0000',
  instagram: '#E4405F',
  x: '#000000',
  tiktok: '#000000',
  threads: '#000000',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  website: '#6B7280',
  email: '#6B7280'
};

/**
 * Get icon component for a platform
 * @param {string} platform - Platform key (e.g., 'instagram', 'spotify')
 * @returns {React.Component|null} Icon component or null
 */
export const getSocialIcon = (platform) => {
  return platformIcons[platform] || FaGlobe;
};

/**
 * Get display name for a platform
 * @param {string} platform - Platform key
 * @returns {string} Display name
 */
export const getPlatformName = (platform) => {
  return platformNames[platform] || platform;
};

/**
 * Get brand color for a platform
 * @param {string} platform - Platform key
 * @returns {string} Hex color code
 */
export const getPlatformColor = (platform) => {
  return platformColors[platform] || '#6B7280';
};

/**
 * Check if a URL/email string is valid for display
 * @param {string} url - URL or email string
 * @param {string} platform - Platform type (optional, for email handling)
 * @returns {boolean} Whether the value is valid
 */
export const isValidUrl = (url, platform = null) => {
  if (!url || url.trim() === '' || url === '#') return false;
  
  // Email addresses don't need URL validation
  if (platform === 'email' || url.includes('@')) {
    return url.includes('@') && url.includes('.');
  }
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Format social links for display
 * Filters out invalid/empty URLs
 * @param {Object} socialLinksData - Raw social links data from API
 * @returns {Array} Array of {platform, url, icon, name} objects
 */
export const formatSocialLinks = (socialLinksData) => {
  if (!socialLinksData) return [];
  
  const links = [];
  const fieldMapping = {
    apple_podcasts_url: 'apple_podcasts',
    spotify_url: 'spotify',
    youtube_url: 'youtube',
    instagram_url: 'instagram',
    x_url: 'x',
    tiktok_url: 'tiktok',
    threads_url: 'threads',
    facebook_url: 'facebook',
    linkedin_url: 'linkedin',
    website_url: 'website',
    email_address: 'email'
  };
  
  for (const [field, platform] of Object.entries(fieldMapping)) {
    const url = socialLinksData[field];
    if (isValidUrl(url, platform)) {
      links.push({
        platform,
        url,
        icon: getSocialIcon(platform),
        name: getPlatformName(platform),
        color: getPlatformColor(platform)
      });
    }
  }
  
  return links;
};

export default {
  platformIcons,
  platformNames,
  platformColors,
  getSocialIcon,
  getPlatformName,
  getPlatformColor,
  isValidUrl,
  formatSocialLinks
};
